import { Component, OnInit, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { PropertyModel } from '../../../../core/models/data/property.model';
import { PropertiesFilter } from '../../../../core/filters/properties.filter';
import { PropertiesService } from '../../../../core/services/property.service';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { AddressTypes } from '../../../../core/enums/data.enums';
import { PropertyListSortOptions } from '../../../../core/enums/sort-options.enums';
import { NoValuePipe } from '../../../../core/pipes/no-value/no-value.pipe';
import { BaseListPageClass } from '../../../../core/base-classes/base-list-page-class';
import { InViewPortDirective } from '../../../../core/directives/in-view-port.directive';

@Component({
  selector: 'ms-charge-schedule-attached-properties',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, NoValuePipe, InViewPortDirective],
  templateUrl: './charge-schedule-attached-properties.component.html',
})
export class ChargeScheduleAttachedPropertiesComponent extends BaseListPageClass implements OnInit {
  private propertiesService = inject(PropertiesService);

  public propertyIds = input.required<number[]>();
  public organizationId = input.required<number>();

  public editRequested = output<void>();

  public properties = signal<PropertyModel[]>([]);
  public count = computed(() => this.properties().length);

  constructor() {
    super();

    effect(() => {
      const ids = this.propertyIds();
      const orgId = this.organizationId();
      if (ids && orgId) {
        untracked(() => this.loadProperties(false));
      }
    });
  }

  public ngOnInit(): void { }

  public onEditClicked(): void {
    this.editRequested.emit();
  }

  public getPhysicalAddress(property: PropertyModel): string | undefined {
    const physical = property.addresses?.find(a => a.addressType === AddressTypes.Physical);
    if (!physical) return undefined;
    const parts = [physical.primaryAddress, physical.secondaryAddress, physical.city, physical.stateProvince?.abbreviation, physical.primaryZip].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : undefined;
  }

  public loadMoreInView(): void {
    if (!this.canLoadMore || !this.hasNext) return;

    this.canLoadMore = false;
    this.skip.update(current => current + this.take);
    this.loadProperties(true);
    this.loadMoreDebounce();
  }

  private async loadProperties(append: boolean): Promise<void> {
    const ids = this.propertyIds();
    const orgId = this.organizationId();

    if (!ids || ids.length === 0 || !orgId) {
      this.properties.set([]);
      this.hasNext = false;
      this.totalCount = 0;
      this.isLoading = false;
      return;
    }

    if (!append) {
      this.resetPagination();
    }

    this.isLoading = true;

    const filter: PropertiesFilter = {
      ...ListConstants.PROPERTIES_FILTER_DEFAULT(),
      propertyIds: ids,
      organizationIds: [orgId]
    };

    try {
      const response = await this.propertiesService.searchProperties(
        filter,
        PropertyListSortOptions.AddressASC,
        this.skip(),
        this.take,
        false
      );

      this.hasNext = response?.data?.hasNext ?? false;
      this.totalCount = response?.data?.total ?? 0;

      const batch = response.data?.data;
      if (response.success && batch) {
        if (append) {
          this.properties.update(current => current.concat(batch));
        } else {
          this.properties.set(batch);
        }
      }
    } catch {
      if (!append) {
        this.properties.set([]);
      }
      this.hasNext = false;
      this.totalCount = 0;
    } finally {
      this.isLoading = false;
    }
  }
}
